<script lang="ts" setup>
import { type Game, GameAssetTypes } from '@game/types'
import type { HeroRotatorTileProps } from '@nimbus/types'
import type { ImageOptions as NuxtImageOptions, ConfiguredImageProviders } from '@nuxt/image'
import { computed, ref, useI18n, useMediaAutoplay, useGameAssets, useImage, useImagePreload, useResizeObserver, shadeColor, useTemplateRef, useDeferredVideoAutoplay } from '#imports'

// @nuxt/image's signature hardcodes the default provider; widen it to opt into 'cloudflare'.
type RotatorTileImageOptions = Omit<NuxtImageOptions, 'provider'> & { provider?: keyof ConfiguredImageProviders }

const props = defineProps<HeroRotatorTileProps>()
const emit = defineEmits<{ startGame: [] }>()

const DEFAULT_FADE_COLOR = '#161210'
// Nuxt Image silently drops sizes entries it can't parse, calc() among them, which left
// phones picking the widest srcset candidate. The xs step is that screen minus the page
// gutters, in plain px.
const BG_SIZES = 'xs:328px sm:387px md:425px'
const BG_MODIFIERS = { width: '345', quality: '70' }

const game = computed<Game | undefined>(() => props.game)

const { t } = useI18n()
const { autoplayAllowed } = useMediaAutoplay()
const { assetUrl, videoAssetUrl, mediaVideoUrl, mediaImageUrl, hasAsset } = useGameAssets()
const img = useImage() as (src: string, modifiers?: NuxtImageOptions['modifiers'], options?: RotatorTileImageOptions) => string

const tileRef = ref<HTMLElement>()
const tileWidth = ref(0)
const tileHeight = ref(0)

function setTileProperties(entries: ResizeObserverEntry[]): void {
    const borderBox = entries[0]?.borderBoxSize?.[0]
    if (!borderBox) return

    tileWidth.value = Math.round(borderBox.inlineSize)
    tileHeight.value = Math.round(borderBox.blockSize)
}

const videoError = ref(false)
const posterFailed = ref(false)
const logoFailed = ref(false)

const enableVideo = computed<boolean>(() => !!props.enableVideo)

const showVideo = computed<boolean>(() => enableVideo.value && autoplayAllowed.value && !videoError.value)

const videoPlaying = ref(false)

const gameTileBg = computed<string>(() => props.backgroundSrc ?? (game.value ? assetUrl(game.value, GameAssetTypes.TILE) : ''))
const gameLogo = computed<string>(() => props.logoSrc ?? (game.value ? assetUrl(game.value, GameAssetTypes.LOGO) : ''))

const hasHeroVideo = computed<boolean>(() =>
    enableVideo.value && (!!props.videoId || !!props.videoSrc || hasAsset(game.value, GameAssetTypes.HERO_VIDEO))
)

const configPosterBg = computed<string | undefined>(() => mediaImageUrl(props.videoPosterSrc) || undefined)
const heroPosterBg = computed<string | undefined>(() =>
    configPosterBg.value
    || (hasHeroVideo.value && hasAsset(game.value, GameAssetTypes.HERO_VIDEO_POSTER)
        ? assetUrl(game.value, GameAssetTypes.HERO_VIDEO_POSTER)
        : undefined)
)

const bgSrc = computed<string | undefined>(() =>
    posterFailed.value ? (gameTileBg.value || undefined) : (heroPosterBg.value || gameTileBg.value || undefined)
)
const bgImgPreset = computed<string>(() => !posterFailed.value && configPosterBg.value ? (props.preset || 'gameservice') : 'gameservice')

// The eager tile's background is the lobby's LCP candidate.
useImagePreload({
    src: () => (props.eager ? bgSrc.value : undefined),
    provider: 'cloudflare',
    preset: bgImgPreset,
    sizes: BG_SIZES,
    modifiers: BG_MODIFIERS,
})

const videoIframeId = computed<string | undefined>(() => props.videoId)
const videoFileSrc = computed<string | undefined>(() =>
    props.videoSrc
        ? mediaVideoUrl(props.videoSrc, tileWidth.value, tileHeight.value)
        : (game.value ? videoAssetUrl(game.value, GameAssetTypes.HERO_VIDEO, tileWidth.value, tileHeight.value) : undefined)
)
const videoPosterUrl = computed<string | undefined>(() =>
    mediaImageUrl(props.videoPosterSrc) || (game.value ? assetUrl(game.value, GameAssetTypes.HERO_VIDEO_POSTER) : undefined)
)
const posterImgPreset = computed<string>(() => props.videoPosterSrc ? (props.preset || 'gameservice') : 'gameservice')

const altTextTitle = computed<string | undefined>(() => game.value?.name ? t('nimbus.heroRotator.slideAlt', { game: game.value.name }) : undefined)

const hasLongName = computed<boolean>(() => {
    const name = game.value?.name?.trim()
    if (!name) return false
    const longestWord = name.split(/\s+/).reduce((max, word) => Math.max(max, word.length), 0)
    return name.length > 16 || longestWord > 9
})

const fadeColor = computed<string>(() => props.color || game.value?.game_hero_colour || DEFAULT_FADE_COLOR)
const artFade = computed<string>(() => {
    const c = fadeColor.value
    return `linear-gradient(100deg, ${shadeColor(c, 0, 0.88)} 0%, ${shadeColor(c, -4, 0.68)} 25%, ${shadeColor(c, -6, 0.42)} 40%, ${shadeColor(c, -8, 0.18)} 52%, transparent 62%)`
})

useResizeObserver(tileRef, setTileProperties)

const videoEl = useTemplateRef<HTMLVideoElement>('videoEl')
useDeferredVideoAutoplay(videoEl)
</script>

<template>
    <div
        ref="tileRef"
        class="hero-rotator-tile"
        role="button"
        tabindex="0"
        @click.prevent="emit('startGame')"
        @keyup.enter="emit('startGame')"
    >
        <NuxtImg
            v-if="bgSrc"
            provider="cloudflare"
            :preset="bgImgPreset"
            class="hero-rotator-tile__bg fit-parent"
            :width="BG_MODIFIERS.width"
            :quality="BG_MODIFIERS.quality"
            :src="bgSrc"
            :alt="altTextTitle"
            :sizes="BG_SIZES"
            :loading="eager ? 'eager' : 'lazy'"
            :fetchpriority="eager ? 'high' : 'low'"
            :preload="eager ? { fetchPriority: 'high' } : false"
            @error="() => posterFailed = true"
        />
        <UIIframe
            v-if="showVideo && videoActive && videoIframeId"
            class="hero-rotator-tile__media"
            :class="{ 'hero-rotator-tile__media--confirmed': videoPlaying }"
            :video-id="videoIframeId"
            :title="altTextTitle"
            :fit-parent="true"
            :aspect-ratio="null"
            loading="eager"
            @load="() => videoPlaying = true"
        />
        <!-- Only the visible slide gets a <video>: autoplay defeats preload="none",
             so an always-mounted element downloads its file at page load. -->
        <video
            v-else-if="showVideo && videoActive && !videoIframeId && videoFileSrc && tileWidth && tileHeight"
            ref="videoEl"
            class="hero-rotator-tile__media"
            :class="{ 'hero-rotator-tile__media--confirmed': videoPlaying }"
            loop
            muted
            playsinline
            disableRemotePlayback
            decoding="async"
            preload="auto"
            :poster="videoPosterUrl ? img(videoPosterUrl, { width: 720, quality: 70 }, { preset: posterImgPreset, provider: 'cloudflare' }) : undefined"
            :src="videoFileSrc"
            :title="altTextTitle"
            @playing="() => videoPlaying = true"
            @error="() => videoError = true"
        />

        <div class="hero-rotator-tile__fade" :style="{ background: artFade }" />

        <div
            class="hero-rotator-tile__text"
            :class="{ 'hero-rotator-tile__text--wide': hasLongName }"
        >
            <NuxtImg
                v-if="gameLogo && !logoFailed"
                provider="cloudflare"
                preset="gameservice"
                class="hero-rotator-tile__logo"
                width="92"
                quality="70"
                :src="gameLogo"
                sizes="80px md:92px"
                :loading="eager ? 'eager' : 'lazy'"
                :fetchpriority="eager ? undefined : 'low'"
                :alt="`${game?.name} logo`"
                @error="() => logoFailed = true"
            />
            <p class="hero-rotator-tile__name" v-text="game?.name" />
        </div>
    </div>
</template>

<style lang="scss" scoped>
.hero-rotator-tile {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: stretch;
    cursor: pointer;
    border-radius: inherit;
    overflow: hidden;

    img,
    video {
        pointer-events: none;
    }

    &__bg,
    &__media {
        position: absolute;
        inset: 0;
        z-index: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: inherit;
    }

    &__media {
        opacity: 0;
        transition: opacity 280ms ease-out;

        &--confirmed {
            opacity: 1;
        }
    }

    &__fade {
        position: absolute;
        inset: 0;
        z-index: 1;
        pointer-events: none;
        transform: translateZ(0);
        -webkit-backface-visibility: hidden;
        backface-visibility: hidden;
    }

    &__text {
        position: relative;
        z-index: 2;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        width: 100%;
        height: 100%;
        padding: 18px;
        transform: translateZ(0);
        -webkit-backface-visibility: hidden;
        backface-visibility: hidden;

        @media (min-width: $screen-md) {
            width: 58%;
        }

        @media (min-width: $screen-sm) {
            width: 65%;
            padding: 28px 0px 20px 28px;
        }

        &--wide {
            @media (min-width: $screen-md) {
                width: 72%;
            }

            @media (min-width: $screen-sm) {
                width: 80%;
            }
        }
    }

    &__logo {
        width: 80px;
        height: auto;
        margin-bottom: 8px;
        filter: drop-shadow(0 2px 8px rgb(0 0 0 / 40%));

        @media (min-width: $screen-md) {
            width: 92px;
        }
    }

    &__name {
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        margin: 0;
        overflow: hidden;
        color: $white;
        font-family: $brand-font-family;
        font-size: 32px;
        line-height: 0.9;
        letter-spacing: 0px;
        text-transform: uppercase;
        font-weight: 700;
        overflow-wrap: break-word;
        text-overflow: ellipsis;
        text-shadow: 0 1px 6px rgb(0 0 0 / 50%);

        @media (min-width: $screen-md) {
            font-size: 36px;
        }
    }
}
</style>
